import { useState, useEffect, useCallback, useMemo } from 'react';
import { analyticsService, PlatformAnalytics, TimeSeriesData, TenantGrowthData, DateRangeParams } from '../services';

interface UsePlatformAnalyticsReturn {
  analytics: PlatformAnalytics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const usePlatformAnalytics = (params: DateRangeParams = {}): UsePlatformAnalyticsReturn => {
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const normalizedParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period,
    }),
    [params.startDate, params.endDate, params.period],
  );

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await analyticsService.getPlatformAnalytics(normalizedParams);
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch analytics'));
    } finally {
      setLoading(false);
    }
  }, [normalizedParams]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    refetch: fetchAnalytics,
  };
};

interface UseTenantGrowthReturn {
  data: TenantGrowthData[];
  loading: boolean;
  error: Error | null;
}

export const useTenantGrowth = (params: DateRangeParams = {}): UseTenantGrowthReturn => {
  const [data, setData] = useState<TenantGrowthData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const normalizedParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period,
    }),
    [params.startDate, params.endDate, params.period],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getTenantGrowth(normalizedParams);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch tenant growth'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [normalizedParams]);

  return { data, loading, error };
};

interface UseRevenueTrendReturn {
  data: TimeSeriesData[];
  loading: boolean;
  error: Error | null;
}

export const useRevenueTrend = (params: DateRangeParams = {}): UseRevenueTrendReturn => {
  const [data, setData] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const normalizedParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period,
    }),
    [params.startDate, params.endDate, params.period],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getRevenueTrend(normalizedParams);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch revenue trend'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [normalizedParams]);

  return { data, loading, error };
};

interface UseUserGrowthReturn {
  data: TimeSeriesData[];
  loading: boolean;
  error: Error | null;
}

export const useUserGrowth = (params: DateRangeParams = {}): UseUserGrowthReturn => {
  const [data, setData] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const normalizedParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period,
    }),
    [params.startDate, params.endDate, params.period],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getUserGrowth(normalizedParams);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch user growth'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [normalizedParams]);

  return { data, loading, error };
};

interface UseRealtimeMetricsReturn {
  metrics: {
    activeUsers: number;
    apiRequestsPerMinute: number;
    ordersToday: number;
    revenueToday: number;
  } | null;
  loading: boolean;
  error: Error | null;
}

export const useRealtimeMetrics = (): UseRealtimeMetricsReturn => {
  const [metrics, setMetrics] = useState<{
    activeUsers: number;
    apiRequestsPerMinute: number;
    ordersToday: number;
    revenueToday: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await analyticsService.getRealtimeMetrics();
        setMetrics(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch realtime metrics'));
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();

    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  return { metrics, loading, error };
};

interface UseApiUsageReturn {
  data: TimeSeriesData[];
  loading: boolean;
  error: Error | null;
}

export const useApiUsage = (params: DateRangeParams = {}): UseApiUsageReturn => {
  const [data, setData] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const normalizedParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period,
    }),
    [params.startDate, params.endDate, params.period],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getApiUsage(normalizedParams);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch API usage'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [normalizedParams]);

  return { data, loading, error };
};
